<template>
  <n-layout class="w-100vw h-100vh overflow-hidden" content-class="flex flex-col">
    <n-layout-header class="flex-shrink-0">
      <EditorToolbar
        :project-id="projectId"
        :project-name="versionPreviewProjectTitle"
        :current-drawing-tool="null"
        :is-sketch-plane-selected="false"
        :shaded-sketch-contours-enabled="false"
        :active-tab-type="null"
        :supports-feature-commands="false"
        :assembly-edit-component-disabled="true"
        readonly-preview
      />
    </n-layout-header>

    <n-layout-content class="flex-1" content-class="flex min-h-0">
      <EditorSideRail
        class="flex h-full"
        :visible-keys="['history']"
        :title-overrides="{ history: '版本历史' }"
        :active-key="activePreviewSidePanel ?? ''"
        @select="handlePreviewSideRailSelect"
      />

      <EditorSidePanelDrawer
        :show="activePreviewSidePanel === 'history'"
        :width="versionPreviewSidePanelWidth"
        :resizing="isVersionPreviewSidePanelResizing"
        resize-aria-label="调整左侧版本列表宽度"
        @resize-start="handleVersionPreviewSidePanelResizePointerDown"
      >
          <VersionPreviewListPanel
            :records="versionPreviewStore.records"
            :selected-version-id="displayedVersionId ?? versionPreviewStore.selectedVersionId"
            :initial-scroll-version-id="initialScrollVersionId"
            :loading="versionPreviewStore.isRecordsLoading"
            :switching-version-id="versionPreviewStore.switchingVersionId"
            @select="handleSelectVersion"
          />

      </EditorSidePanelDrawer>

      <n-split
        direction="horizontal"
        :default-size="0.78"
        :min="0.3"
        :max="0.85"
        class="h-full w-full flex-1 min-w-0"
      >
        <template #1>
          <div class="version-preview-page__viewport">
            <EditorViewportCanvas
              :on-container-ready="handleViewportContainerReady"
              :is-viewport-renderer-ready="isViewportRendererReady"
              :drawing-hint="null"
            />

            <div v-if="isModelLoading" class="model-loading-overlay">
              <n-spin size="large" />
              <span class="model-loading-text">{{ modelLoadingText }}</span>
            </div>

            <ViewportToolbar readonly-preview />
          </div>
        </template>

        <template #2>
          <n-tabs v-model:value="rightPanelTab" type="segment" size="small" class="h-full flex flex-col min-w-200px">
            <n-tab-pane
              name="feature"
              tab="设计树"
              display-directive="show"
              class="h-full w-full flex flex-col overflow-auto"
            >
              <FeatureTree />
            </n-tab-pane>
          </n-tabs>
        </template>
      </n-split>
    </n-layout-content>

    <n-layout-footer class="version-preview-page__footer flex-shrink-0">
      <!-- 零件标签栏暂时隐藏，后续可能恢复；使用 v-if 避免隐藏期间挂载组件及创建监听。 -->
      <PartTabBar
        v-if="false"
        :tabs="glbLoader.partTabsStore.tabs"
        :active-tab-id="glbLoader.displayActivePartTabId.value"
        @select="handlePartTabSelect"
      />
      <EditorFooter
        :selected-object-name="selectedObjectName"
        :engine-ready="isViewportRendererReady"
      />
    </n-layout-footer>
  </n-layout>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useRoute, useRouter } from 'vue-router';
import {
  NLayout,
  NLayoutContent,
  NLayoutFooter,
  NLayoutHeader,
  NSplit,
  NSpin,
  NTabPane,
  NTabs,
  useMessage,
} from 'naive-ui';
import { Viewer } from '@puppycad/cad-core';
import EditorToolbar from '@/components/editor/EditorToolbar.vue';
import EditorSideRail from '@/components/editor/EditorSideRail.vue';
import EditorSidePanelDrawer from '@/components/editor/EditorSidePanelDrawer.vue';
import FeatureTree from '@/components/editor/FeatureTree.vue';
import PartTabBar from '@/components/editor/PartTabBar.vue';
import EditorFooter from '@/components/editor/EditorFooter.vue';
import VersionPreviewListPanel from '@/components/editor/VersionPreviewListPanel.vue';
import EditorViewportCanvas from '@/views/editor/EditorViewportCanvas.vue';
import ViewportToolbar from '@/views/editor/ViewportToolbar.vue';
import { useEditorGlbLoader } from '@/views/editor/useEditorGlbLoader';
import { useEditorViewportInteractionSync } from '@/views/editor/useEditorViewportInteractionSync';
import { syncVersionPreviewIdleViewerMode } from '@/views/version-preview/syncVersionPreviewIdleViewerMode';
import { isImportedModelLoadAbortedError } from '@/services/loaders/imported-model-load-guard';
import {
  VERSION_HISTORY_PANEL_DEFAULT_WIDTH,
  resolveVersionHistoryPanelWidth,
} from '@/views/editor/resolve-version-history-panel-width';
import { useVersionPreviewController } from '@/views/version-preview/useVersionPreviewController';
import { createVersionPreviewService, toProjectCommitSnapshot } from '@/services/version-preview';
import { createCadNodeTreeReloadAdapter } from '@/services/project-model-loading';
import { useVersionPreviewStore, useVersionWorkspaceStore } from '@/stores';
import { useFeatureTreeStore } from '@/hooks/useFeatureTree';
import { useSketchStateStore } from '@/hooks/useSketchState';
import { useViewer } from '@/hooks/useViewer';
import { logger } from '@/utils';
import { resolveOpenProjectRouteContext } from '@/router/open-project-route-context';
import { EditorModelLoadingCoordinator } from '@/core/editor-loading/EditorModelLoadingCoordinator';
import { resolveVersionPreviewInitialSessionInput } from '@/views/version-preview/resolveVersionPreviewInitialSessionInput';
import { getCadNodeTreeSession } from '@/services/api/cad-session';

const route = useRoute();
const router = useRouter();
const message = useMessage();
const versionPreviewStore = useVersionPreviewStore();
const versionWorkspaceStore = useVersionWorkspaceStore();
const featureTreeStore = useFeatureTreeStore();
const sketchStateStore = useSketchStateStore();
const { setViewer, clearViewer } = useViewer();
const { selectedKeys, selectedObject, selectedObjectName } = storeToRefs(featureTreeStore);

const viewportContainerRef = ref<HTMLElement | null>(null);
const isViewportRendererReady = ref(false);
const isModelLoading = ref(false);
const modelLoadingText = ref('');
const projectName = ref('版本预览');
const rightPanelTab = ref('feature');
const activePreviewSidePanel = ref<'history' | null>('history');
const versionPreviewSidePanelWidth = ref(VERSION_HISTORY_PANEL_DEFAULT_WIDTH);
const isVersionPreviewSidePanelResizing = ref(false);
const GLOBAL_LOADING_OWNER = 'version-preview-global';
const VERSION_PREVIEW_RELOAD_LOADING_OWNER = 'version-preview-root-reload';
const loadingOwnerTabId = ref<string>(GLOBAL_LOADING_OWNER);
let viewer: Viewer | null = null;
let versionPreviewController: ReturnType<typeof useVersionPreviewController> | null = null;
let versionPreviewSidePanelResizeStartX = 0;
let versionPreviewSidePanelResizeStartWidth = VERSION_HISTORY_PANEL_DEFAULT_WIDTH;
let isVersionPreviewUnmounted = false;
let initializeVersionPreviewRequestId = 0;
let versionPreviewReloadLoadingOwnerSequence = 0;
let activeVersionPreviewReloadLoadingOwner: string | null = null;
let activeVersionPreviewInitializationLoadingOwner: string | null = null;

const projectId = computed(() => String(route.params.projectId ?? ''));
const openProjectRouteContext = computed(() => resolveOpenProjectRouteContext(route));

const versionPreviewModelLoadingCoordinator = new EditorModelLoadingCoordinator({
  isLoading: isModelLoading,
  loadingText: modelLoadingText,
  owner: loadingOwnerTabId,
  globalOwner: GLOBAL_LOADING_OWNER,
});
const cadNodeTreeReloadAdapter = createCadNodeTreeReloadAdapter({
  getCadNodeTree: getCadNodeTreeSession,
});

const glbLoader = useEditorGlbLoader({
  getScene: () => viewer?.getScene(),
  getViewer: () => viewer,
  projectRootNodeReloadAdapter: cadNodeTreeReloadAdapter,
  showLoading: (text: string, targetTabId?: string) => {
    versionPreviewModelLoadingCoordinator.show(text, targetTabId);
  },
  hideLoading: (targetTabId?: string) => {
    versionPreviewModelLoadingCoordinator.hide(targetTabId);
  },
  resetLoading: (targetTabId: string) => {
    versionPreviewModelLoadingCoordinator.reset(targetTabId);
  },
  showError: (text: string) => message.error(text),
});

const viewportInteractionSync = useEditorViewportInteractionSync({
  getViewer: () => viewer,
  glbFeatureTree: glbLoader.glbFeatureTree,
  selectedKeys,
  selectedObject,
});

/**
 * 解析版本预览页顶部标题。
 *
 * 设计说明：预览页打开的是项目内某个节点的历史版本，不进入可编辑工作区；
 * 因此标题只展示项目与节点身份，避免把分支名误读成当前可提交目标。
 */
const versionPreviewProjectTitle = computed(() => {
  const context = glbLoader.cadSessionStore.openProjectRootDisplayContext;
  if (!context) {
    return projectName.value;
  }

  const projectLabel = context.projectName || (context.projectId ? `项目 ${context.projectId}` : '项目');
  const nodeLabel = context.nodeName || '节点';
  return `${projectLabel} | ${nodeLabel}`;
});

/**
 * 当前 Viewer 中已经成功显示的提交 ID。
 *
 * 设计说明：版本预览页的高亮表达“当前模型属于哪个提交”，不能只依赖用户上一次点击的
 * UI 状态；OpenProject 成功后会同步 `currentCommitId`，因此这里优先使用会话真实提交。
 */
const displayedVersionId = ref<string | null>(null);
/**
 * 初始加载阶段需要滚动到的版本 ID。
 *
 * 设计说明：该值只用于左侧列表首次定位，来源可以是 URL 中的 `versionId`、`adoptedCommitId`，
 * 也可以是 OpenProject 完成后回填的真实 `currentCommitId`；后续用户手动切换版本时不更新它。
 */
const initialScrollVersionId = ref<string | null>(null);

/**
 * 解析刚刚成功落地到 Viewer 的提交 ID。
 *
 * @param fallbackVersionId 会话提交缺失时使用的业务版本 ID。
 * @returns 当前可见模型对应的提交 ID；无法确认时返回 null。
 */
function resolveVersionPreviewLoadedCommitId(fallbackVersionId: string | null = null): string | null {
  const sessionCommitId = glbLoader.cadSessionStore.currentCommitId.trim();
  const fallbackCommitId = fallbackVersionId?.trim() ?? '';
  return sessionCommitId || fallbackCommitId || null;
}

/**
 * 解析 URL 中指定的版本预览目标提交。
 *
 * @returns 当前路由指定的版本 ID；缺省或空白时返回 null。
 */
function resolveVersionPreviewRouteVersionId(): string | null {
  const routeVersionId = typeof route.query.versionId === 'string'
    ? route.query.versionId.trim()
    : '';
  return routeVersionId || null;
}

/**
 * 解析 URL 中指定的外部采纳版本提交。
 *
 * @returns 当前路由指定的采纳提交 ID；缺省或空白时返回 null。
 */
function resolveVersionPreviewRouteAdoptedCommitId(): string | null {
  const routeAdoptedCommitId = typeof route.query.adoptedCommitId === 'string'
    ? route.query.adoptedCommitId.trim()
    : '';
  return routeAdoptedCommitId || null;
}

/**
 * 解析首屏 OpenProject 应打开的提交。
 *
 * 设计说明：versionId 表示当前要预览的历史版本，优先级高于 adoptedCommitId；
 * 当 URL 只声明采纳版本时，首屏直接打开采纳提交，避免先打开分支 head 再二次切换。
 *
 * @returns 首屏应打开的提交 ID；缺省时返回 null。
 */
function resolveVersionPreviewInitialCommitId(): string | null {
  return resolveVersionPreviewRouteVersionId() ?? resolveVersionPreviewRouteAdoptedCommitId();
}

/**
 * 刷新版本预览右侧设计树。
 *
 * 设计说明：`commitPartTabSwitch` 内部会异步触发一次树刷新，但版本预览的高亮语义依赖
 * “模型和设计树都已经切到同一提交”。因此这里显式等待当前激活标签的设计树刷新完成，
 * 再允许外层更新 `displayedVersionId`。
 */
async function refreshVersionPreviewFeatureTree(): Promise<void> {
  await featureTreeStore.refreshTree(glbLoader.partTabsStore.activeTabId);
}

/**
 * 尽力刷新版本预览右侧设计树。
 *
 * 设计说明：根模型已经成功提交到 Viewer 后，设计树刷新失败不能再走 controller 的
 * “切换失败回退”路径，否则会出现模型已是新提交但列表仍高亮旧提交。这里把设计树失败
 * 降级为可定位日志和用户提示，继续让真实模型提交成为当前显示版本。
 *
 * @param versionId 已经切换成功的目标版本 ID。
 * @param context 版本切换请求上下文，用于避免陈旧请求弹出错误提示。
 */
async function refreshVersionPreviewFeatureTreeBestEffort(
  versionId: string,
  context: { isCurrent: () => boolean },
): Promise<void> {
  try {
    await refreshVersionPreviewFeatureTree();
  } catch (error) {
    if (!context.isCurrent()) {
      return;
    }
    logger.error('[VersionPreview] refresh feature tree after model switch failed', {
      versionId,
      error,
    });
    message.error('刷新设计树失败，当前模型已切换');
  }
}

/**
 * 带设计树 loading 状态地刷新版本预览设计树。
 *
 * 设计说明：初始化阶段没有独立版本切换 owner，但仍需要避免右侧树晚于模型空白完成；
 * 这里只负责树 loading，不接管模型 overlay。
 */
async function refreshVersionPreviewFeatureTreeWithLoading(): Promise<void> {
  featureTreeStore.isTreeLoading = true;
  try {
    await refreshVersionPreviewFeatureTree();
  } finally {
    featureTreeStore.isTreeLoading = false;
  }
}

/**
 * 记录视口容器引用。
 *
 * @param element 视口容器元素。
 */
function handleViewportContainerReady(element: HTMLElement | null): void {
  viewportContainerRef.value = element;
}

/**
 * 处理版本 Preview 左侧 rail 选择。
 *
 * 版本 Preview 只开放版本历史入口；再次点击同一入口时收起列表，保证它和编辑器侧栏
 * 具有一致的 tab 抽屉语义。
 *
 * @param key 左侧 rail 入口 key。
 */
function handlePreviewSideRailSelect(key: string): void {
  if (key !== 'history') {
    return;
  }
  activePreviewSidePanel.value = activePreviewSidePanel.value === 'history' ? null : 'history';
}

/**
 * 开始拖拽版本列表抽屉宽度。
 *
 * @param event 指针按下事件。
 */
function handleVersionPreviewSidePanelResizePointerDown(event: PointerEvent): void {
  event.preventDefault();
  handleVersionPreviewSidePanelResizePointerUp();
  isVersionPreviewSidePanelResizing.value = true;
  versionPreviewSidePanelResizeStartX = event.clientX;
  versionPreviewSidePanelResizeStartWidth = versionPreviewSidePanelWidth.value;

  window.addEventListener('pointermove', handleVersionPreviewSidePanelResizePointerMove);
  window.addEventListener('pointerup', handleVersionPreviewSidePanelResizePointerUp);
  window.addEventListener('pointercancel', handleVersionPreviewSidePanelResizePointerUp);
}

/**
 * 拖拽过程中同步版本列表抽屉宽度。
 *
 * @param event 指针移动事件。
 */
function handleVersionPreviewSidePanelResizePointerMove(event: PointerEvent): void {
  const nextWidth = versionPreviewSidePanelResizeStartWidth
    + event.clientX
    - versionPreviewSidePanelResizeStartX;
  versionPreviewSidePanelWidth.value = resolveVersionHistoryPanelWidth(nextWidth);
}

/**
 * 结束拖拽并释放全局指针监听。
 */
function handleVersionPreviewSidePanelResizePointerUp(): void {
  isVersionPreviewSidePanelResizing.value = false;
  window.removeEventListener('pointermove', handleVersionPreviewSidePanelResizePointerMove);
  window.removeEventListener('pointerup', handleVersionPreviewSidePanelResizePointerUp);
  window.removeEventListener('pointercancel', handleVersionPreviewSidePanelResizePointerUp);
}

/**
 * 判断本次版本预览初始化是否仍然拥有回写资格。
 *
 * @param requestId 初始化请求编号。
 * @returns 是否仍是当前有效初始化请求。
 */
function isActiveVersionPreviewInitialization(requestId: number): boolean {
  return !isVersionPreviewUnmounted && initializeVersionPreviewRequestId === requestId;
}

/**
 * 等待 DOM 容器拥有可用尺寸。
 *
 * @param element 视口容器元素。
 * @param timeout 等待超时时间。
 * @returns 容器尺寸就绪 Promise。
 */
function waitForContainerSize(element: HTMLElement, timeout = 3000): Promise<void> {
  return new Promise((resolve) => {
    if (element.clientWidth > 0 && element.clientHeight > 0) {
      resolve();
      return;
    }

    const observer = new ResizeObserver(() => {
      if (element.clientWidth > 0 && element.clientHeight > 0) {
        observer.disconnect();
        resolve();
      }
    });

    observer.observe(element);
    window.setTimeout(() => {
      observer.disconnect();
      resolve();
    }, timeout);
  });
}

/**
 * 初始化只读预览 Viewer。
 */
async function initializeViewer(requestId: number): Promise<void> {
  if (!viewportContainerRef.value || viewer) {
    return;
  }

  const viewportContainer = viewportContainerRef.value;
  await waitForContainerSize(viewportContainer);
  if (!isActiveVersionPreviewInitialization(requestId) || viewer) {
    return;
  }

  const createdViewer = new Viewer(viewportContainer, {
    gridSize: 0.01,
    gridDivisions: 0.05,
    cameraPosition: [0.1,0.1,0.1],
  });
  viewer = createdViewer;
  setViewer(createdViewer);
  await createdViewer.whenReady();
  if (!isActiveVersionPreviewInitialization(requestId) || viewer !== createdViewer) {
    return;
  }
  viewportInteractionSync.register();
  isViewportRendererReady.value = true;
}

/**
 * 回写路由版本 query。
 *
 * @param versionId 版本 ID。
 */
function replaceVersionQuery(versionId: string): void {
  void router.replace({
    query: {
      ...route.query,
      versionId,
    },
  });
}

/**
 * 同步版本预览页当前标签对应的空闲 Viewer 模式。
 *
 * 设计说明：
 * 1. 版本预览页只读，但设计树 hover / click 仍然需要 cad-core 进入正确视觉状态；
 * 2. 装配体标签必须落到 Assembly 模式，才能让 imported-model 树节点高亮被 AssemblyTransitionState 消费；
 * 3. 该同步只改变视图交互状态，不改变 CAD 主模型状态，因此不接入 Undo/Redo。
 */
function syncPreviewIdleViewerMode(): void {
  const currentViewer = viewer;
  if (!currentViewer) {
    return;
  }

  syncVersionPreviewIdleViewerMode({
    activeTabType: glbLoader.partTabsStore.activeTab?.type ?? null,
    setSketchViewerMode: (mode) => sketchStateStore.setViewerMode(mode),
    setViewerMode: (mode) => currentViewer.setMode(mode),
  });
}

/**
 * 处理用户选择版本。
 *
 * @param versionId 版本 ID。
 */
function handleSelectVersion(versionId: string): void {
  void versionPreviewController?.selectVersion(versionId);
}

/**
 * 处理预览页底部零件/装配体标签切换。
 *
 * @param tabId 目标标签页 ID。
 */
function handlePartTabSelect(tabId: string): void {
  void (async () => {
    await glbLoader.handlePartTabSelect(tabId);
    syncPreviewIdleViewerMode();
  })();
}

/**
 * 归一化版本预览提交列表使用的仓库 ID。
 *
 * @param repositoryId OpenProject 路由或响应中的仓库 ID。
 * @returns 可传给 ListCommits 的具体仓库 ID。
 */
function normalizeVersionPreviewRepositoryId(
  repositoryId: bigint | null | undefined,
): bigint | null {
  if (repositoryId === null || repositoryId === undefined || repositoryId === 0n) {
    return null;
  }
  return repositoryId;
}

/**
 * 归一化版本预览提交列表使用的分支过滤 ID。
 *
 * @param branchId OpenProject 路由或响应中的分支 ID。
 * @returns 可传给 ListCommits 的具体分支 ID。
 */
function normalizeVersionPreviewBranchId(branchId: bigint | null | undefined): bigint | undefined {
  if (branchId === null || branchId === undefined || branchId === 0n) {
    return undefined;
  }
  return branchId;
}

/**
 * 解析版本预览真实接口需要的仓库上下文。
 *
 * 设计说明：
 * 1. URL query 中的 repositoryId/branchId 是用户显式打开目标，优先使用；
 * 2. 当 URL 缺省时，OpenProject 会返回后端实际打开的根仓库和分支，作为版本定位上下文；
 * 3. adoptedCommitId 是外部 URL 明确声明的采纳版本，不能由当前 Viewer 显示提交推导。
 *
 * @returns 版本预览接口上下文；仓库缺失时返回 null。
 */
function resolveVersionPreviewRepositoryContext() {
  const sessionInput = openProjectRouteContext.value.sessionInput;
  const repositoryId = normalizeVersionPreviewRepositoryId(sessionInput.repositoryId)
    ?? normalizeVersionPreviewRepositoryId(glbLoader.cadSessionStore.currentRepositoryId);
  const branchId = normalizeVersionPreviewBranchId(sessionInput.branchId)
    ?? normalizeVersionPreviewBranchId(glbLoader.cadSessionStore.currentBranchId);
  const adoptedCommitId = resolveVersionPreviewRouteAdoptedCommitId();
  if (repositoryId === null) {
    return null;
  }

  return {
    repositoryId,
    branchId,
    ...(adoptedCommitId ? { adoptedCommitId } : {}),
  };
}

/**
 * 开始版本预览初始化 loading，并返回本次初始化独占的 owner。
 *
 * 设计说明：初始化阶段会跨越 Viewer、OpenProject、默认模型和版本记录四个异步边界。
 * 使用独立 owner 可以让旧初始化晚返回时只清理自己的 loading，不会误关后续版本切换的遮罩。
 *
 * @param requestId 初始化请求编号。
 * @returns 本次初始化 loading owner。
 */
function beginVersionPreviewInitializationLoading(requestId: number): string {
  const loadingOwner = `${GLOBAL_LOADING_OWNER}:init:${requestId}`;
  activeVersionPreviewInitializationLoadingOwner = loadingOwner;
  versionPreviewStore.isRecordsLoading = true;
  featureTreeStore.isTreeLoading = true;
  versionPreviewModelLoadingCoordinator.show('正在加载项目模型...', loadingOwner);

  return loadingOwner;
}

/**
 * 释放版本预览初始化阶段持有的 loading。
 *
 * 设计说明：模型遮罩可能已经被后续历史版本切换接管，因此这里只在 owner 仍匹配时关闭遮罩；
 * 记录列表 loading 属于初始化请求本身，只有当前初始化 owner 仍有效时才允许回收。
 *
 * @param loadingOwner 本次初始化开始时返回的 owner。
 */
function releaseVersionPreviewInitializationLoading(loadingOwner: string): void {
  const isInitializationOwnerActive = activeVersionPreviewInitializationLoadingOwner === loadingOwner;
  if (isInitializationOwnerActive) {
    activeVersionPreviewInitializationLoadingOwner = null;
    versionPreviewStore.isRecordsLoading = false;
  }

  if (versionPreviewModelLoadingCoordinator.isOwnedBy(loadingOwner)) {
    featureTreeStore.isTreeLoading = false;
    versionPreviewModelLoadingCoordinator.hide(loadingOwner);
    return;
  }

  if (isInitializationOwnerActive && !activeVersionPreviewReloadLoadingOwner && !isModelLoading.value) {
    featureTreeStore.isTreeLoading = false;
  }
}

/**
 * 取消当前历史版本重载 loading。
 *
 * 设计说明：当用户在 A -> B 的历史版本加载过程中点回已经显示的 A，旧的 B 加载会继续在底层
 * 走到下一次 `context.isCurrent()` 检查后才收尾；页面此时应该立刻恢复到 A 的可交互状态，
 * 因此只释放当前历史重载 owner 持有的模型遮罩和设计树 loading，不影响其它 owner。
 */
function cancelActiveVersionPreviewReloadLoading(): void {
  const loadingOwner = activeVersionPreviewReloadLoadingOwner;
  if (!loadingOwner) {
    return;
  }

  activeVersionPreviewReloadLoadingOwner = null;
  if (!versionPreviewModelLoadingCoordinator.isOwnedBy(loadingOwner)) {
    return;
  }

  featureTreeStore.isTreeLoading = false;
  versionPreviewModelLoadingCoordinator.hide(loadingOwner);
}

/**
 * 初始化版本 Preview 页面。
 */
async function initializeVersionPreview(): Promise<void> {
  const requestId = ++initializeVersionPreviewRequestId;
  const initialCommitId = resolveVersionPreviewInitialCommitId();
  displayedVersionId.value = initialCommitId;
  initialScrollVersionId.value = initialCommitId;
  const initializationLoadingOwner = beginVersionPreviewInitializationLoading(requestId);

  try {
    await initializeViewer(requestId);
    if (!isActiveVersionPreviewInitialization(requestId)) {
      return;
    }
    projectName.value = await glbLoader.initSessionAndLoadFiles(
      resolveVersionPreviewInitialSessionInput({
        sessionInput: openProjectRouteContext.value.sessionInput,
        initialCommitId,
      }),
    );
    if (!isActiveVersionPreviewInitialization(requestId)) {
      return;
    }
    await glbLoader.loadDefaultTab();
    if (!isActiveVersionPreviewInitialization(requestId)) {
      return;
    }
    await refreshVersionPreviewFeatureTreeWithLoading();
    if (!isActiveVersionPreviewInitialization(requestId)) {
      return;
    }
    displayedVersionId.value = resolveVersionPreviewLoadedCommitId(
      initialCommitId ?? versionPreviewStore.selectedVersionId,
    );
    initialScrollVersionId.value = displayedVersionId.value;
    if (displayedVersionId.value) {
      versionWorkspaceStore.enterHistoryPreview(displayedVersionId.value);
    }
    syncPreviewIdleViewerMode();

    versionPreviewController = useVersionPreviewController({
      store: versionPreviewStore,
      workspaceStore: versionWorkspaceStore,
      service: createVersionPreviewService({
        getRepositoryContext: resolveVersionPreviewRepositoryContext,
      }),
      getProjectId: () => projectId.value,
      getRouteVersionId: resolveVersionPreviewRouteVersionId,
      getDisplayedVersionId: () => displayedVersionId.value,
      replaceVersionQuery,
      cancelActiveReload: cancelActiveVersionPreviewReloadLoading,
      reloadSnapshot: async (snapshot, context) => {
        const loadingText = `正在加载历史版本：${snapshot.title}`;
        const loadingOwner = `${VERSION_PREVIEW_RELOAD_LOADING_OWNER}:${++versionPreviewReloadLoadingOwnerSequence}`;
        activeVersionPreviewReloadLoadingOwner = loadingOwner;
        versionPreviewModelLoadingCoordinator.show(loadingText, loadingOwner);
        featureTreeStore.isTreeLoading = true;
        try {
          await glbLoader.switchProjectModelRootSnapshot({
            snapshot: toProjectCommitSnapshot(snapshot),
            context,
            loadingText,
            loadingOwner,
            preserveLoadingAfterApply: true,
          });
          if (!context.isCurrent()) {
            return;
          }
          await refreshVersionPreviewFeatureTreeBestEffort(snapshot.versionId, context);
          if (!context.isCurrent()) {
            return;
          }
          displayedVersionId.value = resolveVersionPreviewLoadedCommitId(snapshot.versionId);
          syncPreviewIdleViewerMode();
        } finally {
          if (activeVersionPreviewReloadLoadingOwner === loadingOwner) {
            activeVersionPreviewReloadLoadingOwner = null;
          }
          if (versionPreviewModelLoadingCoordinator.isOwnedBy(loadingOwner)) {
            featureTreeStore.isTreeLoading = false;
            versionPreviewModelLoadingCoordinator.hide(loadingOwner);
          }
        }
      },
      notifyError: (text) => message.error(text),
    });
    await versionPreviewController.initialize();
    if (!isActiveVersionPreviewInitialization(requestId)) {
      return;
    }
    syncPreviewIdleViewerMode();
  } catch (error) {
    if (!isActiveVersionPreviewInitialization(requestId) || isImportedModelLoadAbortedError(error)) {
      return;
    }
    logger.error('[VersionPreview] initialize failed', error);
    message.error('加载版本预览失败');
  } finally {
    releaseVersionPreviewInitializationLoading(initializationLoadingOwner);
  }
}

onMounted(() => {
  void initializeVersionPreview();
});

onUnmounted(() => {
  isVersionPreviewUnmounted = true;
  initializeVersionPreviewRequestId += 1;
  // 页面销毁时立即释放本页持有的 loading，避免跨路由复用的 store 残留忙碌态。
  const initializationLoadingOwner = activeVersionPreviewInitializationLoadingOwner;
  if (initializationLoadingOwner) {
    releaseVersionPreviewInitializationLoading(initializationLoadingOwner);
  }
  cancelActiveVersionPreviewReloadLoading();
  handleVersionPreviewSidePanelResizePointerUp();
  versionPreviewController?.dispose();
  versionPreviewController = null;
  viewportInteractionSync.dispose();
  glbLoader.dispose();
  viewer?.dispose();
  viewer = null;
  clearViewer();
  versionPreviewStore.reset();
  versionWorkspaceStore.enterEditingMode();
});
</script>

<style scoped>
.version-preview-page__viewport {
  position: relative;
  width: 100%;
  height: 100%;
}

.version-preview-page__footer {
  border-top: 1px solid var(--n-border-color);
  background: var(--n-color);
}

.model-loading-overlay {
  position: absolute;
  inset: 0;
  z-index: 20;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  backdrop-filter: blur(2px);
}

.model-loading-text {
  font-size: 13px;
  color: var(--n-text-color);
}
</style>
